/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import PageType from 'linced-app/enums/page-type';
import PostTypes from '@linc-technologies/linced-common/enums/post-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import StaffAccessLevel from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import {
  getTagCountsForPage,
  getTagPieData,
} from '@linc-technologies/linced-common/utils/tags';
import { setDiff } from '@ember/object/computed';

export default class PageController extends Controller.extend(EditPostMixin) {
  queryParams = [
    'authorIds',
    'autoFilter',
    'endTime',
    'groupIds',
    'offset',
    'searchQuery',
    'startTime',
    'status',
  ];

  PageType = PageType;
  PostTypes = PostTypes;
  StaffAccessLevel = StaffAccessLevel;

  // Services
  @service
  fetch;

  @service
  options;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  students;

  @service
  toaster;

  // State
  @tracked
  authorIds = [];

  @tracked
  autoFilter = '';
  @tracked
  endTime = '';
  @tracked
  groupIds = [];
  @tracked
  offset = 0;
  searchQuery = '';
  @tracked
  startTime = '';
  @tracked status = 0;

  // Dynamic
  @computed('authorIds.[],model.authors.[]')
  get selectedAuthors() {
    return this.authorIds.map((id) => {
      return this.store.peekRecord('person', id);
    });
  }

  @setDiff('model.authors', 'selectedAuthors')
  remainingAuthors;

  @computed('remainingAuthors')
  get sortedRemainingAuthors() {
    return this.remainingAuthors
      .slice()
      .sort(
        (a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') ||
          (a.firstName || '').localeCompare(b.firstName || ''),
      );
  }

  pageDetailsOpen = false;

  init() {
    super.init(...arguments);
    this.isMobile = isMobile();
  }

  // canManagePermissions determines whether to show the Page Permission
  // management UI to the current user.
  @computed('model.{page.type,person.id}', 'session.{personId,scopes.[]}')
  get canManagePermissions() {
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Allow LINC-ED staff to see/manage Page Permissions for pages.
      return true;
    }
    if (this.model.page.type === PageType.SCHOOL) {
      // Only staff members with page access level "Access Control Level" can manage permissions.
      return (
        this.session.person.pageAccessLevel ===
        StaffAccessLevel.ACCESS_CONTROL_LEVEL
      );
    }
  }

  // staffCanView calculates whether the current user can view posts on this page,
  // if the page in question is a Staff page.
  @computed('canManagePermissions', 'model.{person.id,pagePermissions.[]}')
  get staffCanView() {
    if (this.canManagePermissions) {
      // If you can manage the permissions, you can also view the Page.
      return true;
    }
    // If it's not your own page, you'll only get any PagePermissions if you
    // have been given access to this Page for this Person.
    return !isEmpty(this.model.pagePermissions);
  }

  @computed('model.{page.tagCategories.[],posts.@each.tags}')
  get tagCounts() {
    return getTagCountsForPage(this.model.page, this.model.posts);
  }

  @tracked
  printDialogOpen = false;

  @tracked
  isMobile;

  // Functions
  @action
  addAuthor(author) {
    if (this.authorIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 authors. Please remove one before adding more.',
      );
    } else {
      this.authorIds = [...this.authorIds, author.get('id')];
    }
  }

  @action
  removeAuthor(author) {
    let id = author.get('id');
    this.authorIds = this.authorIds.filter((authorId) => authorId !== id);
  }

  addGroup(group) {
    if (this.groupIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 groups. Please remove one before adding more.',
      );
    } else {
      this.groupIds = [...this.groupIds, group.id];
    }
  }

  removeGroup(group) {
    this.groupIds = this.groupIds.filter((id) => id !== group.id);
  }

  @action
  setStartTime(startTime) {
    this.startTime = startTime;
    this.autoFilter = '';
    this.offset = 0;
  }

  @action
  setEndTime(endTime) {
    this.endTime = endTime;
    this.autoFilter = '';
    this.offset = 0;
  }

  @action
  clear() {
    this.startTime = '';
    this.endTime = '';
    this.autoFilter = '';
    this.authorIds = [];
    this.offset = 0;
  }

  @action
  filterDay() {
    this.autoFilter !== 'day'
      ? (this.autoFilter = 'day')
      : (this.autoFilter = '');
    this.startTime = '';
    this.endTime = '';
    this.offset = 0;
  }

  @action
  filterWeek() {
    this.autoFilter !== 'week'
      ? (this.autoFilter = 'week')
      : (this.autoFilter = '');
    this.startTime = '';
    this.endTime = '';
    this.offset = 0;
  }

  @action
  filterSevenDays() {
    this.autoFilter !== '7-days'
      ? (this.autoFilter = '7-days')
      : (this.autoFilter = '');
    this.startTime = '';
    this.endTime = '';
    this.offset = 0;
  }

  filterPostStatus(newStatus) {
    this.status !== newStatus ? (this.status = newStatus) : (this.status = 0);
  }

  @action
  nextPage() {
    this.incrementProperty('offset', 20);
  }

  @action
  prevPage() {
    this.decrementProperty('offset', 20);
  }

  @action
  refresh() {
    this.get('model.posts').update();
  }

  @action
  getTagPieData() {
    return getTagPieData.call(this, ...arguments);
  }

  @action
  incrementGradeChanges() {
    this.incrementProperty('numGradeChanges');
  }

  @action
  printPage() {
    this.printDialogOpen = true;
  }

  @action
  closePrintDialog() {
    this.printDialogOpen = false;
  }

  @action
  shouldDisablePostActions(post) {
    if (this.model.page.type !== PageType.STAFF) {
      // Don't mess with the actions on non-staff pages.
      return false;
    }
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Allow LINC-ED staff to manage all posts.
      return false;
    }

    // Only the author should be able to edit/delete/etc a given post.
    return post.author_id !== this.session.personId;
  }
}
